import type { ReactNode } from "react";
import { useOutletContext } from "react-router-dom";
import { Badge, type PortalDashboardMedicareConnection } from "@dearbill/shared";
import { CaregiverCard } from "./account/CaregiverCard";
import { fmtSyncedAt } from "./dashboardFormat";
import { HomeCard } from "./home/HomeCard";
import type { DashboardOutletContext } from "./MemberDashboard";

/**
 * The Account content, rendered inside `MemberLayout` from the dashboard the
 * outlet context carries. The page title is the `<h1>` and each tile carries
 * an `<h2>`: the Medicare Account Connection card takes the left slot of a
 * two-column grid from `lg` and the Authorized Caregivers card the right one;
 * below `lg` they stack in that order. The Medicare tile offers no connect or
 * disconnect control (the member manages the connection on Medicare.gov); the
 * caregiver tile is read-only on a caregiver session.
 */
export function AccountScreen(): ReactNode {
  const { data, reload, session, onSignOut } = useOutletContext<DashboardOutletContext>();
  return (
    <>
      <div className="flex flex-col gap-3">
        <h1 className="text-h2 text-midnight font-serif">Account Overview</h1>
        <p className="text-body text-slate-teal">
          Manage your connected Medicare account and caregiver access.
        </p>
      </div>
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <MedicareAccountCard connection={data.medicareConnection} />
        <CaregiverCard
          caregiver={data.caregiver}
          readOnly={data.role === "caregiver"}
          session={session}
          reload={reload}
          onSignOut={onSignOut}
        />
      </div>
    </>
  );
}

/**
 * The member's Blue Button connection status, which the dashboard keys on the
 * session's patient. Connected, the body names Medicare.gov as the place to
 * disconnect or update credentials; that link opens in a new tab so the portal
 * stays open, and sr-only text announces the new tab to assistive tech.
 */
function MedicareAccountCard({
  connection,
}: {
  connection: PortalDashboardMedicareConnection;
}): ReactNode {
  const { connected } = connection;
  const synced = connected ? fmtSyncedAt(connection.lastSyncedAt) : null;
  return (
    <HomeCard title="Medicare Account Connection" titleSize="lg">
      <div className="flex flex-wrap items-center gap-3">
        <Badge tone={connected ? "active" : "neutral"}>
          {connected ? "Connected" : "Not connected"}
        </Badge>
        {synced !== null && <p className="text-body-sm text-slate-teal">Last synced: {synced}</p>}
      </div>
      <p className="text-body-sm text-slate-teal">
        {connected ? (
          <>
            DearBill is connected to your official Medicare.gov account. If you ever need to
            disconnect or update your credentials, please do so directly through{" "}
            <a
              href="https://www.medicare.gov/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-text hover:text-ember-dark underline"
            >
              Medicare.gov<span className="sr-only"> (opens in a new tab)</span>
            </a>
            .
          </>
        ) : (
          "Your Medicare account isn't connected to DearBill."
        )}
      </p>
    </HomeCard>
  );
}
